<template>
  <div class="new-users-panel">
    <div class="row">
      <div class="form-group col-xs-6 col-sm-6 col-md-6">
        <label class="required">Country</label>
        <span title="The audience query will only work for the country selected here."><i class="fa fa-info-circle"></i></span>
        <nv-select class="form-control" v-model="gRowObj.country_id" @change="selectedCountry">
          <option value="">--Choose a Country--</option>
          <option v-for="data in countryList" :key="data.country_id" :value="data.country_id">{{ data.country_name }}</option>
        </nv-select>
        <span v-if="submitted && !gRowObj.country_id" class="text-red">Please choose a country.</span>
      </div>
      <div class="form-group col-xs-6 col-sm-6 col-md-6">
        <label class="required">Customer</label>
        <nv-select class="form-control" v-model="gRowObj.client_id" @change="selectedClient">
          <option value="">--Choose a Customer--</option>
          <option v-for="data in clientList" :key="data.id" :value="data.id">{{ data.name }}</option>
        </nv-select>
        <span v-if="submitted && !gRowObj.client_id" class="text-red">Please choose a customer.</span>
      </div>
    </div>

    <div class="row">
      <div class="form-group col-xs-8 col-sm-8 col-md-8">
        <label class="required">Segment Name</label>
        <input type="text" v-model="gRowObj.audience_name" class="form-control" placeholder="Segment Name" />
        <span v-if="submitted && !(gRowObj.audience_name || '').trim()" class="text-red">Please click Generate Segment Name or enter one.</span>
      </div>
      <div class="form-group col-xs-4 col-sm-4 col-md-4">
        <button type="button" class="btn btn-primary new-users-generate-btn" @click="generateAudience">Generate Segment Name</button>
      </div>
    </div>

    <div class="row">
      <div class="form-group col-xs-6 col-sm-6 col-md-6">
        <label class="required">Google Sheet URL</label>
        <input type="text" v-model="gRowObj.spreadsheetURL" class="form-control" placeholder="Google Sheet URL" />
        <span v-if="submitted && !(gRowObj.spreadsheetURL || '').trim()" class="text-red">Please enter the Google Sheet URL.</span>
      </div>
      <div class="form-group col-xs-6 col-sm-6 col-md-6">
        <label class="checkbox-inline new-users-jor-check">
          <input type="checkbox" v-model="gRowObj.isUploadInJOR" @change="onJorToggle" />
          Onboard users to JOR?
          <span title="Schedules these users directly into a journey's reminder flow — only needed for that."><i class="fa fa-info-circle"></i></span>
        </label>
      </div>
    </div>

    <div class="row" v-if="gRowObj.isUploadInJOR">
      <div class="form-group col-xs-6 col-sm-6 col-md-6">
        <label class="required">User Journey</label>
        <treeselect
          placeholder="--Choose a journey--"
          :multiple="false"
          :options="conversationApps"
          v-model="gRowObj.conversation" />
        <span v-if="submitted && !gRowObj.conversation" class="text-red">Please choose a journey.</span>
        <span class="help-block">JOR onboarding requires a journey-type app — only journeys are listed here.</span>
      </div>
    </div>

    <div class="action-button">
      <button type="button" class="btn btn-primary" @click="uploadUsers">Submit</button>
    </div>

    <div class="new-users-instructions">
      <label>Instructions</label>
      <ul>
        <li>Upload the Google Sheet into <a href="https://drive.google.com/drive/folders/1Vb7nmtQoqEvBFjW3LqoSUtfptnx5u6sq" target="_blank">this folder</a>.</li>
        <li>Columns must be in this exact order: <strong>Phone Number, Language, Gender, Age, Pincode, Country</strong>.</li>
        <li>Phone Number: include the country code, no special characters.</li>
        <li>Language: one of <code>eng, hi, swa, hau</code>.</li>
        <li>Gender: one of <code>man, woman, nonbinary</code>.</li>
        <li>Country: capitalize the first letter (e.g. {{ activeCountryList.join(', ') }}).</li>
        <li>Reference sheet: <a href="https://docs.google.com/spreadsheets/d/18HVveQmCpHEAnddx3Neaxem1wITtPlMCr0QpDq_e03I/edit?usp=sharing" target="_blank">demo template</a>.</li>
      </ul>
    </div>

    <BlockUI v-if="loading"><i class="fa fa-cog fa-spin fa-3x fa-fw"></i><br/>Please wait...</BlockUI>
  </div>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'
import moment from 'moment'
import apiold from '../../../../utils/api'
import { formatDate, getActiveCountryList } from '../../../../utils/utilities'
import { instanceCountriesMapping } from '../../../../config/constants'
import { getAccessToken } from '../../../../utils/auth'

export default {
  name: 'new-users-upload-panel',
  components: {
    treeselect: Treeselect
  },
  data () {
    return {
      activeCountryList: [],
      currentDate: moment(new Date()).format('YYYY_MM_DD_HH_mm_ss'),
      loading: false,
      countryList: [],
      clientList: [],
      conversationApps: [],
      conversationAppCopy: [],
      gRowObj: {
        country_id: '',
        client_id: '',
        spreadsheetURL: '',
        isUploadInJOR: false,
        audience_name: null,
        appPackage: '',
        conversation: null
      },
      submitted: false,
      countryName: '',
      countryAlise: '',
      clientName: ''
    }
  },
  async mounted () {
    this.loading = true
    await this.fetchCountryList()
    await this.fetchClientList()
    await this.loadApps()
    this.activeCountryList = await getActiveCountryList()
    this.loading = false
  },
  methods: {
    async fetchCountryList () {
      try {
        const response = await apiold.buildRequest('getCountryList', 'POST')
        const domainName = window.location.hostname
        const possibleCountries = (instanceCountriesMapping[domainName] || {}).countries || []
        this.countryList = (response.data.payload || [])
          .filter(item => item.country_name.toLowerCase() !== 'global' && possibleCountries.includes(item.country_name.toLowerCase()))
        this.countryList.forEach(item => { item.country_name = item.country_name.replace(/ /g, '_') })
      } catch (error) {
        this.$swal({ type: 'error', text: error.response ? error.response.data.payload.message : 'An error occurred' })
      }
    },
    async fetchClientList () {
      try {
        const response = await apiold.buildRequest('getClientData', 'POST')
        this.clientList = response.data.payload || []
      } catch (error) {
        this.$swal({ type: 'error', text: error.response ? error.response.data.payload.message : 'An error occurred' })
      }
    },
    async loadApps () {
      try {
        const response = await apiold.buildRequest('app?deployed=true', 'GET')
        this.conversationApps = (response.data.payload.apps || []).map(convo => ({
          id: convo.name,
          type: convo.config && convo.config.type,
          label: `${convo.name} - ${formatDate(convo.deployedDate, 'd/M/Y hh:ss')}`
        }))
        this.conversationAppCopy = this.conversationApps
      } catch (error) {
        this.$swal({ type: 'error', text: error.response ? error.response.data.payload.message : 'An error occurred' })
      }
    },
    selectedCountry () {
      const country = this.countryList.find(c => c.country_id === this.gRowObj.country_id)
      if (country) {
        this.countryAlise = country.country_alise.charAt(0).toUpperCase() + country.country_alise.slice(1)
        this.countryName = country.country_name
      }
    },
    selectedClient () {
      const client = this.clientList.find(c => c.id === this.gRowObj.client_id)
      if (client) this.clientName = client.name
    },
    generateAudience () {
      if (this.clientName && this.countryAlise) {
        this.gRowObj.audience_name = `${this.clientName}_${this.countryAlise}_NewUsers_${this.currentDate}`
      }
    },
    onJorToggle () {
      this.gRowObj.conversation = null
      if (this.gRowObj.isUploadInJOR) {
        this.gRowObj.appPackage = 'journey'
        this.conversationApps = this.conversationAppCopy.filter(item => item.type === 'journey' || item.type === 'journeyReminder')
      } else {
        this.gRowObj.appPackage = ''
        this.conversationApps = this.conversationAppCopy
      }
    },
    async getSmsAppIdByName (appName) {
      try {
        const response = await apiold.buildRequest('/getSmsAppIdByName', 'POST', { appName })
        return response
      } catch (error) {
        this.$swal({ type: 'error', text: error.response ? error.response.data.payload.message : 'An error occurred' })
        return null
      }
    },
    isFormValid () {
      this.submitted = true
      const jorFieldsValid = !this.gRowObj.isUploadInJOR || (this.gRowObj.appPackage && this.gRowObj.conversation)
      return !!(
        this.gRowObj.country_id && this.gRowObj.client_id && jorFieldsValid &&
        (this.gRowObj.audience_name || '').trim() && (this.gRowObj.spreadsheetURL || '').trim()
      )
    },
    async uploadUsers () {
      if (!this.isFormValid()) return
      await this.addUsers()
      this.submitted = false
    },
    async addUsers () {
      this.loading = true
      const param = { ...this.gRowObj }
      param.audience_name = param.audience_name.trim()
      param.keyword_name = null

      param.spreadsheetURL = param.spreadsheetURL.trim()
      param.spreadsheetId = param.spreadsheetURL.split('/d/')[1] ? param.spreadsheetURL.split('/d/')[1].split('/edit')[0] : ''
      param.country = this.countryName
      param.clientName = this.clientName
      param.user_source = `${this.clientName}_${this.countryAlise}`

      // conversation/conversationId are only meaningful for JOR onboarding — skip resolving them otherwise
      if (this.gRowObj.isUploadInJOR && param.conversation) {
        const resSmsApp = await this.getSmsAppIdByName(`${param.conversation}`)
        if (resSmsApp && resSmsApp.status === 200 && resSmsApp.data && resSmsApp.data.payload && resSmsApp.data.payload.length) {
          param.conversationId = resSmsApp.data.payload[0].appId
        }
      } else {
        param.conversation = null
        param.conversationId = null
      }

      try {
        const { data } = await apiold.buildRequest('csvToDB', 'POST', param, { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken()}` } })
        this.loading = false
        if (data.payload && data.payload.type === 'success') {
          this.$swal({
            width: 600,
            title: data.payload.text,
            type: 'success',
            html: `<table style="table-layout:fixed; width:100%; font-size: initial">
                    <tr><td style="width:40%">Segment</td><td><a target="_blank" href="/audience-builder/upload?queryId=${data.payload.audienceQueryId}">${data.payload.audience_query_name}</a></td></tr>
                    <tr><td>New users: (${data.payload.successAccountIds.length})</td><td><div style="max-height: 100px; overflow-y: auto;">${data.payload.successAccountIds.join('\n')}</div></td></tr>
                    <tr><td>Existing users: (${data.payload.existingAccountIds.length})</td><td><div style="max-height: 100px; overflow-y: auto;">${data.payload.existingAccountIds.join('\n')}</div></td></tr>
                  </table>`
          }).then(() => {
            this.resetForm()
          })
          this.$emit('segment-saved')
        } else {
          this.$swal({
            title: data.payload ? data.payload.type : 'error',
            type: 'error',
            html: `<div>${data.payload ? data.payload.text : 'An error occurred'}</div>`
          })
        }
      } catch (error) {
        this.loading = false
        this.$swal({ type: 'error', text: error.response ? error.response.data.payload.message : 'An error occurred' })
      }
    },
    resetForm () {
      this.gRowObj = {
        country_id: '',
        client_id: '',
        spreadsheetURL: '',
        isUploadInJOR: false,
        audience_name: null,
        appPackage: '',
        conversation: null
      }
      this.currentDate = moment(new Date()).format('YYYY_MM_DD_HH_mm_ss')
      this.countryName = ''
      this.countryAlise = ''
      this.clientName = ''
      this.conversationApps = this.conversationAppCopy
    }
  }
}
</script>

<style scoped>
.new-users-panel {
  margin-bottom: 20px;
}
.new-users-generate-btn {
  margin-top: 25px;
  white-space: nowrap;
}
.new-users-jor-check {
  margin-top: 23px;
}
.new-users-instructions {
  margin-top: 24px;
  padding: 14px 16px;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  color: #4a5568;
}
.new-users-instructions ul {
  margin: 8px 0 0;
  padding-left: 20px;
}
.required:after {
  content: " *";
  color: red;
}
</style>



// WEBPACK FOOTER //
// NewUsersUploadPanel.vue?56d7882f